import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { 
  updateTabBadge, 
  updateMultipleTabBadges, 
  clearTabBadge 
} from '@/store/slices/navigationSlice';
import { TabBadgeUpdate, NavigationContextData } from '@/types/navigation';

/**
 * Custom hook for managing tab badges in the mobile navigation
 * Provides methods to update, clear, and calculate tab badges based on app state
 */
export function useTabBadges() {
  const dispatch = useAppDispatch();
  
  const { badges } = useAppSelector(state => state.navigation);
  const { totalCount: conversationCount } = useAppSelector(state => state.conversations);
  const { newsContext } = useAppSelector(state => state.news);
  const { currentLocation } = useAppSelector(state => state.location);

  // Update a single tab badge
  const updateSingleBadge = useCallback((
    tabId: string, 
    badge: number | string | null
  ) => {
    dispatch(updateTabBadge({ tabId, badge }));
  }, [dispatch]);

  // Update multiple tab badges at once
  const updateBadges = useCallback((updates: TabBadgeUpdate[]) => {
    dispatch(updateMultipleTabBadges(updates));
  }, [dispatch]);

  // Clear a specific tab badge
  const clearBadge = useCallback((tabId: string) => {
    dispatch(clearTabBadge(tabId));
  }, [dispatch]);

  // Calculate badges based on current app state
  const calculateBadges = useCallback((): TabBadgeUpdate[] => {
    const updates: TabBadgeUpdate[] = [];

    // Discover tab badge - show news count if available
    if (newsContext && newsContext.totalItems > 0) {
      updates.push({
        tabId: 'discover',
        badge: newsContext.totalItems > 99 ? '99+' : newsContext.totalItems.toString()
      });
    } else {
      updates.push({ tabId: 'discover', badge: null });
    }

    // Conversations tab badge - show saved conversation count
    if (conversationCount > 0) {
      updates.push({
        tabId: 'conversations',
        badge: conversationCount > 99 ? '99+' : conversationCount.toString()
      });
    } else {
      updates.push({ tabId: 'conversations', badge: null });
    }

    // Settings tab badge - show if location is not available
    if (!currentLocation || !currentLocation.coordinates) {
      updates.push({ tabId: 'settings', badge: '!' });
    } else {
      updates.push({ tabId: 'settings', badge: null });
    }

    return updates;
  }, [newsContext, conversationCount, currentLocation]);

  // Update all badges based on current app state
  const refreshBadges = useCallback(() => {
    const updates = calculateBadges();
    updateBadges(updates);
  }, [calculateBadges, updateBadges]);

  // Get badge for a specific tab
  const getBadge = useCallback((tabId: string): number | string | undefined => {
    return badges[tabId];
  }, [badges]);

  // Check if a tab has a badge
  const hasBadge = useCallback((tabId: string): boolean => {
    const badge = badges[tabId];
    return badge !== undefined && badge !== null && badge !== '';
  }, [badges]);

  // Format badge for display (handle numbers vs strings)
  const formatBadge = useCallback((badge: number | string | undefined): string => {
    if (badge === undefined || badge === null || badge === '') {
      return '';
    }
    
    if (typeof badge === 'number') {
      return badge > 99 ? '99+' : badge.toString();
    }
    
    return badge.toString();
  }, []);

  // Create context data for badge calculations
  const createContextData = useCallback((): NavigationContextData => {
    return {
      location: {
        latitude: currentLocation?.coordinates?.lat || 0,
        longitude: currentLocation?.coordinates?.lng || 0,
        city: currentLocation?.locationInfo?.city,
        weather: currentLocation?.weather?.condition,
      },
      newsCount: newsContext?.totalItems || 0,
      conversationCount: conversationCount || 0,
      unreadNotifications: 0, // This would be from a notifications system
    };
  }, [currentLocation, newsContext, conversationCount]);

  return {
    // State
    badges,
    
    // Single badge operations
    updateSingleBadge,
    clearBadge,
    getBadge,
    hasBadge,
    formatBadge,
    
    // Bulk operations
    updateBadges,
    refreshBadges,
    calculateBadges,
    
    // Utilities
    createContextData,
  };
}

/**
 * Main API function for updating tab badges
 * Updates badges based on current application state and context
 */
export async function updateTabBadges(
  contextData?: NavigationContextData
): Promise<{
  success: boolean;
  updatedBadges: Record<string, number | string>;
  error?: string;
}> {
  try {
    const updates: Record<string, number | string> = {};

    if (contextData) {
      // Update discover tab with news count
      if (contextData.newsCount > 0) {
        updates.discover = contextData.newsCount > 99 ? '99+' : contextData.newsCount.toString();
      }

      // Update conversations tab with conversation count
      if (contextData.conversationCount > 0) {
        updates.conversations = contextData.conversationCount > 99 ? '99+' : contextData.conversationCount.toString();
      }

      // Update settings tab if location is missing
      if (!contextData.location.latitude || !contextData.location.longitude) {
        updates.settings = '!';
      }

      // Update with unread notifications if any
      if (contextData.unreadNotifications > 0) {
        updates.settings = contextData.unreadNotifications > 99 ? '99+' : contextData.unreadNotifications.toString();
      }
    }

    return {
      success: true,
      updatedBadges: updates,
    };
  } catch (error) {
    console.error('Failed to update tab badges:', error);
    return {
      success: false,
      updatedBadges: {},
      error: error instanceof Error ? error.message : 'Failed to update badges',
    };
  }
}

export default useTabBadges;